import { FC, ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    DataTable,
    DataTableActionButton,
    DataTableActions,
    DataTableColumn,
    DataTableEntityCell,
} from "../../../../../design-system";
import { formatNumberWithSuffix } from "../../../../../common/Number";
import { ScoreTag } from "../../../../../common/components/ScoreTag";
import { ExpirationDateCell } from "../../../../../common/components/ExpirationDateCell";
import { ListCardTag } from "../../../../../common/components/ListCard";
import { OrderForm, canDoOnDeal, orderFormScore } from "../../../../../types/OrderFormTypes";
import { ProjectAction } from "../../../../../types/ProjectTypes";
import { buyerBriefRefPath } from "../../../../../common/BuyerBriefLink";
import { OwnerCell } from "../../../../../common/components/OwnerCell";
import { ownerFilter } from "../../../../../common/components/OwnerFilter";
import { useUsers } from "../../../../../common/useUsers";
import { reassignProjectOwner } from "../../../../../common/api/ProjectClient";
import { Savings } from "./DealSavings";
import {
    dealStatusPresentation,
    StatusProgressCell,
} from "../../../../project/components/ProjectStatusCell";
import { getDayDiffFromDates } from "../../../../../types/TimeTypes";

// The Status filter's choices, in pipeline order.
const STATUS_OPTIONS = [
    "Not started",
    "In negotiation",
    "Ready to sign",
    "Signed",
    "Cancelled",
].map((value) => ({ value, label: value }));

// The Expiration filter's buckets, the same thresholds the cell colours by.
const EXPIRATION_OPTIONS = ["Past", "Within 30 days", "Within 60 days", "Later", "None"].map(
    (value) => ({ value, label: value })
);

const expirationBucket = (deal: OrderForm): string => {
    if (!deal.orderEndDate) return "None";
    const days = getDayDiffFromDates(new Date(), new Date(deal.orderEndDate));
    if (days <= 0) return "Past";
    if (days <= 30) return "Within 30 days";
    if (days <= 60) return "Within 60 days";
    return "Later";
};

interface DealsTableProps {
    deals: OrderForm[];
    /** Controls first in the table's toolbar (the Deals / RFXs switch). */
    controls?: ReactNode;
    /** Called after a row changes (owner reassigned) so the list reloads. */
    onUpdate?: () => void;
    emptyText?: ReactNode;
}

const dealPath = (deal: OrderForm) =>
    deal.projectId ? `/project/${deal.projectId}` : `/order/${deal.id}`;

const vendorNameOf = (deal: OrderForm) => deal.vendor?.name || deal.vendorKey || "";

const dealStatus = (deal: OrderForm) => dealStatusPresentation(deal.negotiationStatus);

/** The negotiations view's deal list. */
export const DealsTable: FC<DealsTableProps> = ({ deals, controls, onUpdate, emptyText }) => {
    const navigate = useNavigate();
    // For the Owner filter's chip to name the picked user.
    const users = useUsers();

    const columns: DataTableColumn<OrderForm>[] = [
        {
            key: "deal",
            title: "Deal",
            width: "20%",
            sort: "text",
            sortValue: (deal) => deal.name || vendorNameOf(deal),
            render: (deal) => {
                const vendorName = vendorNameOf(deal);
                return (
                    <DataTableEntityCell
                        imageSrc={deal.vendor?.hero}
                        imageFallback={(vendorName || "?").charAt(0).toUpperCase()}
                        name={<Link to={dealPath(deal)}>{deal.name || vendorName}</Link>}
                        detail={vendorName}
                    />
                );
            },
        },
        {
            key: "status",
            title: "Status",
            // Pipeline order: not started, in negotiation, ready, signed.
            sort: "number",
            sortValue: (deal) => dealStatus(deal)?.stage,
            filter: {
                type: "enum",
                options: STATUS_OPTIONS,
                value: (deal) => dealStatus(deal)?.label,
            },
            render: (deal) => <StatusProgressCell status={dealStatus(deal)} />,
        },
        {
            key: "savings",
            title: "Savings",
            sort: "number",
            sortValue: (deal) => Number(deal.targetedSavings || 0),
            render: (deal) => <Savings deal={deal} />,
        },
        {
            key: "value",
            title: "Baseline TCV",
            sort: "number",
            sortValue: (deal) => deal.value,
            render: (deal) => (
                <ListCardTag color="grey">${formatNumberWithSuffix(deal.value)}</ListCardTag>
            ),
        },
        {
            key: "score",
            title: "Deal score",
            sort: "number",
            sortValue: (deal) => orderFormScore(deal),
            render: (deal) => {
                const score = orderFormScore(deal);
                return score === undefined ? (
                    <>–</>
                ) : (
                    <ScoreTag score={score} type="percentage" showGrade />
                );
            },
        },
        {
            key: "owner",
            title: "Owner",
            defaultHidden: true,
            sort: "text",
            sortValue: (deal) => deal.projectOwner?.name,
            filter: ownerFilter((deal) => deal.projectOwner?.id, users),
            // The owner is the project's, editable by whoever may reassign it, as on the hub.
            render: (deal) =>
                deal.projectId ? (
                    <OwnerCell
                        ownerId={deal.projectOwner?.id}
                        ownerName={deal.projectOwner?.name}
                        canEdit={canDoOnDeal(deal, ProjectAction.REASSIGN)}
                        onSave={async (ownerId) => {
                            await reassignProjectOwner(deal.projectId!, ownerId);
                        }}
                        onUpdate={onUpdate}
                    />
                ) : null,
        },
        {
            key: "expiration",
            title: "Expiration",
            sort: "date",
            sortValue: (deal) => deal.orderEndDate,
            filter: { type: "enum", options: EXPIRATION_OPTIONS, value: expirationBucket },
            render: (deal) => (
                <ExpirationDateCell date={deal.orderEndDate?.toString()} orderFormId={deal.id} />
            ),
        },
        {
            key: "actions",
            title: "Actions",
            actions: true,
            render: (deal) => {
                const briefPath = buyerBriefRefPath(deal.vendor?.buyerBrief);
                return (
                    <DataTableActions>
                        <DataTableActionButton
                            icon="forms"
                            label="Project"
                            onClick={() => navigate(dealPath(deal))}
                        />
                        <DataTableActionButton
                            icon="briefs"
                            label="Buyer Brief"
                            disabled={!briefPath}
                            onClick={() => briefPath && navigate(briefPath)}
                        />
                    </DataTableActions>
                );
            },
        },
    ];

    return (
        <DataTable<OrderForm>
            columns={columns}
            rows={deals}
            rowKey={(deal) => deal.id}
            controls={controls}
            searchText={(deal) => [deal.name, vendorNameOf(deal)]}
            searchPlaceholder="Search deals"
            emptyText={emptyText}
            storageKey="deals"
        />
    );
};
